/**
 * Centralized Error Handler
 *
 * Translates API errors into user-friendly messages and provides error reporting/logging.
 */

import { logger } from './logger';

// Error types
export enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}

// Error interface
export interface AppError {
  type: ErrorType;
  userMessage: string;
  technicalMessage: string;
  statusCode?: number;
  details?: unknown;
  timestamp: Date;
}

// Error logger type
type ErrorLogger = (error: AppError) => void;

// Default error logger (uses structured logger)
const defaultErrorLogger: ErrorLogger = (error: AppError) => {
  logger.error(error.technicalMessage, new Error(error.technicalMessage), {
    errorType: error.type,
    userMessage: error.userMessage,
    statusCode: error.statusCode,
    details: error.details,
    timestamp: error.timestamp.toISOString(),
  });
};

let customErrorLogger: ErrorLogger | null = null;

/**
 * Set a custom error logger
 */
export function setErrorLogger(logger: ErrorLogger) {
  customErrorLogger = logger;
}

/**
 * Log error using configured logger
 */
function logError(error: AppError) {
  if (customErrorLogger) {
    customErrorLogger(error);
  } else {
    defaultErrorLogger(error);
  }
}

/**
 * Parse Axios error and convert to AppError
 */
export function parseApiError(error: unknown): AppError {
  const timestamp = new Date();

  // Network errors (no response)
  if (!(error as any).response) {
    // Timeout error
    if ((error as any).code === 'ECONNABORTED' || (error as any).message?.includes('timeout')) {
      const timeoutError: AppError = {
        type: ErrorType.TIMEOUT,
        userMessage: 'Request timed out. Please check your connection and try again.',
        technicalMessage: (error as any).message || 'Request timeout',
        timestamp,
      };
      logError(timeoutError);
      return timeoutError;
    }

    // Other network errors
    const networkError: AppError = {
      type: ErrorType.NETWORK,
      userMessage: 'Network error. Please check your internet connection.',
      technicalMessage: (error as any).message || 'Network error',
      timestamp,
    };
    logError(networkError);
    return networkError;
  }

  // Server responded with error
  const response = (error as any).response;
  const status = response.status;
  const data = response.data;

  // Get error message from response
  const serverMessage = data?.error || data?.message || 'Unknown server error';

  switch (status) {
    case 400: {
      // Validation error
      const validationError: AppError = {
        type: ErrorType.VALIDATION,
        userMessage: serverMessage || 'Invalid input. Please check your data and try again.',
        technicalMessage: serverMessage,
        statusCode: status,
        details: data?.details || data,
        timestamp,
      };
      logError(validationError);
      return validationError;
    }

    case 401: {
      // Authentication error
      const authError: AppError = {
        type: ErrorType.AUTHENTICATION,
        userMessage: 'Your session has expired. Please log in again.',
        technicalMessage: serverMessage,
        statusCode: status,
        timestamp,
      };
      logError(authError);
      return authError;
    }

    case 403: {
      // Authorization error
      const forbiddenError: AppError = {
        type: ErrorType.AUTHORIZATION,
        userMessage: "You don't have permission to perform this action.",
        technicalMessage: serverMessage,
        statusCode: status,
        timestamp,
      };
      logError(forbiddenError);
      return forbiddenError;
    }

    case 404: {
      // Not found error
      const notFoundError: AppError = {
        type: ErrorType.NOT_FOUND,
        userMessage: 'The requested resource was not found.',
        technicalMessage: serverMessage,
        statusCode: status,
        timestamp,
      };
      logError(notFoundError);
      return notFoundError;
    }

    case 429: {
      // Rate limit error
      const rateLimitError: AppError = {
        type: ErrorType.VALIDATION,
        userMessage: 'Too many requests. Please wait a moment and try again.',
        technicalMessage: serverMessage,
        statusCode: status,
        timestamp,
      };
      logError(rateLimitError);
      return rateLimitError;
    }

    case 500:
    case 502:
    case 503:
    case 504: {
      // Server errors
      const serverError: AppError = {
        type: ErrorType.SERVER,
        userMessage: 'Server error. Please try again later.',
        technicalMessage: serverMessage,
        statusCode: status,
        timestamp,
      };
      logError(serverError);
      return serverError;
    }

    default: {
      // Unknown error
      const unknownError: AppError = {
        type: ErrorType.UNKNOWN,
        userMessage: 'An unexpected error occurred. Please try again.',
        technicalMessage: serverMessage,
        statusCode: status,
        timestamp,
      };
      logError(unknownError);
      return unknownError;
    }
  }
}

/**
 * Get user-friendly error message from AppError
 */
export function getUserMessage(error: AppError | Error): string {
  if ('type' in error) {
    return error.userMessage;
  }
  return error.message || 'An unexpected error occurred.';
}

/**
 * Check if error is authentication error (should redirect to login)
 */
export function isAuthError(error: AppError): boolean {
  return error.type === ErrorType.AUTHENTICATION;
}

/**
 * Check if error is network error (retry might work)
 */
export function isNetworkError(error: AppError): boolean {
  return error.type === ErrorType.NETWORK || error.type === ErrorType.TIMEOUT;
}

/**
 * Check if error is validation error (user input issue)
 */
export function isValidationError(error: AppError): boolean {
  return error.type === ErrorType.VALIDATION;
}

/**
 * Check if error is server error (backend issue)
 */
export function isServerError(error: AppError): boolean {
  return error.type === ErrorType.SERVER;
}

/**
 * Format error for display in UI
 */
export function formatErrorForDisplay(error: AppError): {
  title: string;
  message: string;
  actionable: boolean;
} {
  let title = 'Error';
  let actionable = false;

  switch (error.type) {
    case ErrorType.TIMEOUT:
      title = 'Request Timeout';
      actionable = true;
      break;
    case ErrorType.NETWORK:
      title = 'Network Error';
      actionable = true;
      break;
    case ErrorType.VALIDATION:
      title = 'Invalid Input';
      actionable = true;
      break;
    case ErrorType.AUTHENTICATION:
      title = 'Session Expired';
      actionable = true;
      break;
    case ErrorType.AUTHORIZATION:
      title = 'Access Denied';
      actionable = false;
      break;
    case ErrorType.NOT_FOUND:
      title = 'Not Found';
      actionable = false;
      break;
    case ErrorType.SERVER:
      title = 'Server Error';
      actionable = true;
      break;
    default:
      title = 'Error';
      actionable = false;
  }

  return {
    title,
    message: error.userMessage,
    actionable,
  };
}
